/**
 * @fileoverview Componente Organism para la composición del área de chat en el inbox.
 * Orquesta la visualización de chat, notas, canales cerrados y modos de conversación, con lógica optimizada y modular.
 * @module InboxComposerMC
 * @category Component - Organism
 * @since 1.0.0
 * @version 2.1.0
 */
import React, { memo, Suspense, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
  ChatModeDropdownMC,
  ChatNoteComponentMC,
  ClosedChannelMC,
  ChatComponentMC,
  ToggleMiaResponsesMC,
  FepBadgeMC
} from './components';
import { useSidebarModalMC } from '../../hooks/shared/ui/useSidebarModalMC';
import BlockedUserMessageMC from './components/BlockedUserMessageMC';
import DisconnectedChannelMessageMC from './components/DisconnectedChannelMessageMC';
import MiaActiveMessageMC from './components/MiaActiveMessageMC';
import useChatAvailability from '../../hooks/inbox/useChatAvailability';
import useSelectedConversation from '../../hooks/shared/useSelectedConversation';
import useInboxChatModeUI from '../../hooks/inbox/ui/useInboxChatModeUI';
import ensureInboxCustomer from '../../../services/inbox/ensureInboxCustomerService';
import { toSidebarPlatformKey } from '../../../services/inbox/inboxMsChatPlatform';
/**
 * @typedef {Object} InboxComposerMCProps
 * @description Este componente no recibe props directos, utiliza datos de Redux y hooks compartidos.
 */

/**
 * Componente Organism para la composición del área de chat en el inbox.
 *
 * Atomic Design: Organism
 * - Orquesta ChatComponentMC, ChatNoteComponentMC, ClosedChannelMC, ChatModeDropdownMC, ToggleMiaResponsesMC
 * - Permite alternar entre chat y notas, mostrar canales cerrados y mensajes de usuario bloqueado
 * - Utiliza TailwindCSS con .tw-wrapper
 * - Optimizado con memo, useMemo y hooks de UI
 *
 * @component InboxComposerMC
 * @category Component - Organism
 * @param {InboxComposerMCProps} props - No recibe props directos
 * @returns {JSX.Element} Elemento JSX renderizado
 * @example
 * <InboxComposerMC />
 * @see ChatComponentMC
 * @see ChatNoteComponentMC
 * @see ClosedChannelMC
 * @see ChatModeDropdownMC
 * @see ToggleMiaResponsesMC
 * @since 1.0.0
 * @version 2.1.0 - Optimización de diseño y documentación
 */

const InboxComposerMC = memo(() => {
  const { t } = useTranslation('InboxMC');
  const dispatch = useDispatch();
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);
  const {
    customer: customerData
  } = useSelector((state) => state.inboxRightReducer);
  const conversation = useSelectedConversation();
  const { hasBalance, isChatClosed, isMiaBlockingComposer } = useChatAvailability({ conversation });
  // UI Layer: estado de modo de chat (chat/note)
  const { selectedChatMode, handleToggleChatMode } = useInboxChatModeUI();

  const isWhatsappConversation = useMemo(
    () => toSidebarPlatformKey(conversation?.platform) === 'whatsapp',
    [conversation?.platform]
  );

  const typeClosedChannel = useMemo(() => {
    if (isWhatsappConversation) {
      return hasBalance ? "whatsapp" : "balance";
    }
    return "facebook";
  }, [hasBalance, isWhatsappConversation]);

  const messageClosedChannel = useMemo(() => {
    if (isWhatsappConversation) {
      return hasBalance
        ? t('chatWindow.closedChannel.whatsappInactive24h')
        : t('chatWindow.closedChannel.insufficientBalance');
    }
    return t('chatWindow.closedChannel.facebookInactive7days');
  }, [hasBalance, isWhatsappConversation, t]);

  // Hook compartido para sidebar/modal (API simplificada y flexible)
  const { openModal } = useSidebarModalMC({
    refData: {
      customer: customerData,
      channel: conversation?.platform,
      platform: conversation?.platform
    }
  });

  const handleClosedChannelModalOpen = useCallback(async (componentProps, componentKey) => {
    const needsCustomer = componentKey === 'NewChat' || componentKey === 'ManageDeal';
    if (!needsCustomer || !conversation?.id) {
      openModal(componentProps, componentKey);
      return;
    }
    const result = await dispatch(ensureInboxCustomer(conversation));
    if (!result.success) return;
    if (componentKey === 'ManageDeal') {
      openModal({
        ...componentProps,
        deal: {
          ...componentProps.deal,
          customer: result.customer
        }
      }, componentKey);
      return;
    }
    openModal(componentProps, componentKey);
  }, [conversation, dispatch, openModal]);

  // Disconnected channel: the microservice rejects sending but still accepts
  // internal notes, so the mode selector is kept and only the chat composer is
  // replaced.
  const isChannelDisconnected = !!conversation?.platformRetailerDisconnected;
  const isUserBlocked = !!conversation?.userIsBlocked;

  return (
    <Suspense fallback={<div className="loading" />}>
      <div className="tw-wrapper tw-shrink-0">
        <div className="tw-flex tw-items-center tw-gap-2 tw-pl-4 tw-h-8">
          <ChatModeDropdownMC
            selectedChatMode={selectedChatMode}
            handleToggleChatMode={handleToggleChatMode}
          />
          {!isUserBlocked && !isChatClosed && !isChannelDisconnected && (
            <ToggleMiaResponsesMC />
          )}
          <div className="tw-flex">
            <FepBadgeMC conversation={conversation} />
          </div>
        </div>
        {selectedChatMode === 'chat' && (
          <div className="tw-flex tw-flex-row tw-border-t tw-border-[#e7e7e7] tw-pl-4 tw-pr-4 tw-pb-1 tw-pt-1">
            {isUserBlocked && (
              <BlockedUserMessageMC />
            )}
            {!isUserBlocked && isChannelDisconnected && (
              <DisconnectedChannelMessageMC />
            )}
            {!isUserBlocked && !isChannelDisconnected && isChatClosed && (
              <ClosedChannelMC
                type={typeClosedChannel}
                slug={retailer_info?.slug}
                message={messageClosedChannel}
                onClick={handleClosedChannelModalOpen}
              />
            )}
            {!isUserBlocked && !isChannelDisconnected && !isChatClosed && isMiaBlockingComposer && (
              <MiaActiveMessageMC />
            )}
            {!isUserBlocked && !isChannelDisconnected && !isChatClosed && !isMiaBlockingComposer && (
              <div className="tw-w-full tw-mt-1">
                <ChatComponentMC
                  openModal={openModal}
                  isChatClosed={isChatClosed}
                />
              </div>
            )}
          </div>
        )}
        {selectedChatMode === 'notes' && (
          <ChatNoteComponentMC
            handleToggleChatMode={() => handleToggleChatMode('chat')}
          />
        )}
      </div>
    </Suspense>
  );
});

InboxComposerMC.displayName = 'InboxComposerMC';

export default InboxComposerMC;
